import { useAsync } from 'react-use';

import { generatedAPI, type DashboardTemplate } from '../api/clients/dashboardtemplates/v1alpha1';

// The generated client only re-exports a subset of hooks by name; the lazy query
// variant is generated by RTK Query on the api object, so we reach it from there.
const { useLazyListDashboardTemplateQuery } = generatedAPI;

// Fetch a large page to keep the number of round-trips low; the server may still
// cap it and return a continue token, which the drain loop below follows.
const PAGE_LIMIT = 100;

interface UseAllDashboardTemplatesResult {
  items: DashboardTemplate[];
  isLoading: boolean;
  error: unknown;
}

/**
 * Loads every dashboard template by following the Kubernetes list continue token
 * until it is empty, then returns the combined list.
 *
 * We deliberately drain all pages here because the templates UI does all of its
 * filtering (search, tags, creators, sort) on the client in `useTemplateFiltering`.
 * Client-side filters can only be correct if the full set is loaded — a single
 * server page would silently hide any template beyond the default page size.
 */
export function useAllDashboardTemplates({ skip }: { skip: boolean }): UseAllDashboardTemplatesResult {
  const [trigger] = useLazyListDashboardTemplateQuery();

  const { value, loading, error } = useAsync(async () => {
    if (skip) {
      return [];
    }

    const all: DashboardTemplate[] = [];
    let continueToken: string | undefined;

    do {
      const page = await trigger({ limit: PAGE_LIMIT, continue: continueToken }).unwrap();
      all.push(...page.items);
      continueToken = page.metadata.continue;
    } while (continueToken);

    return all;
  }, [skip, trigger]);

  return {
    items: value ?? [],
    isLoading: loading,
    error,
  };
}
